import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
	Button,
	GenericField,
	LargeTextField,
	StatefulImage,
	SwitchField,
	TextField,
	WidgetLabel,
} from '../../../rapid-ui'
import { logger } from '../../../utilities'
import { ConformedBranding, MediaAsset } from '../../../views'
import { WidgetColorField } from '../../widgets'

/**
 * Normalize URL by trimming whitespace and adding https:// prefix if needed.
 * Returns null for empty or whitespace-only inputs.
 */
function normalizeUrl(url: string): string | null {
	const trimmed = url.trim()
	if (!trimmed) {
		return null
	}

	// Add https:// if no protocol is specified
	if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
		return `https://${trimmed}`
	}

	return trimmed
}

/**
 * Validate URL and return error message if invalid.
 * Returns null for valid URLs or empty/whitespace-only inputs.
 */
function validateUrl(url: string): string | null {
	const normalized = normalizeUrl(url)

	// Empty is OK, just disables the button
	if (!normalized) {
		return null
	}

	// Use Zod to validate URL format
	const urlSchema = z.string().url()
	const result = urlSchema.safeParse(normalized)

	if (!result.success) {
		return 'branding.urlValidationError'
	}

	return null
}

export type CornerRadius = 'none' | 'small' | 'medium' | 'large'

export type BrandEditorProps = {
	branding: ConformedBranding | null
	onChange: (branding: ConformedBranding) => void

	enableScanning?: boolean

	isGenerating?: boolean
	isPending?: boolean

	nameValue?: string
	onNameChange?: (value: string) => void

	urlValue: string
	onUrlChange: (value: string) => void
	onScanUrl?: (url: string, onSuccess: () => void, onError: (errorKey: string) => void) => void
	/** Translation key for why the last scan of this branding failed. */
	scanError?: string | null

	onLogoUpload?: (logo: File, callback: (asset: MediaAsset) => void, onFail?: () => void) => void

	cornerRadiusValue?: CornerRadius
	onCornerRadiusChange?: (value: CornerRadius) => void

	/** A campaign's brand copy ignores this preference, so only the brand library shows it. */
	showKeyImagePreference?: boolean
}

export function BrandEditor(
	{
		branding,
		onChange,
		enableScanning = true,
		isGenerating = false,
		isPending = false,
		nameValue,
		onNameChange,
		urlValue,
		onUrlChange,
		onScanUrl,
		scanError = null,
		onLogoUpload,
		cornerRadiusValue,
		onCornerRadiusChange,
		showKeyImagePreference = false,
	}: BrandEditorProps,
) {
	const { t } = useTranslation('common')

	const cornerRadiusOptions = [
		{ id: 'none' as const, label: t('branding.cornerRadius.none') },
		{ id: 'small' as const, label: t('branding.cornerRadius.small') },
		{ id: 'medium' as const, label: t('branding.cornerRadius.medium') },
		{ id: 'large' as const, label: t('branding.cornerRadius.large') },
	] as const

	const handlePaletteColorChange = (
		field: 'foreground' | 'background' | 'accentForeground' | 'accentBackground',
		value: string,
	) => {
		if (!branding) { return }
		onChange({ ...branding, [field]: value })
	}

	const [isChangingLogo, setIsChangingLogo] = useState(false)

	const handleLogoUpload = (file: File) => {
		setIsChangingLogo(true)
		onLogoUpload?.(file, (asset) => {
			setIsChangingLogo(false)
			if (fileInputRef.current) {
				fileInputRef.current.value = ''
			}
			// Update the branding with the new logo
			if (branding) {
				onChange({
					...branding,
					logoId: asset.id,
					logo: asset,
				})
			}
		}, () => {
			// onFail: Reset the loading state
			setIsChangingLogo(false)
			if (fileInputRef.current) {
				fileInputRef.current.value = ''
			}
		})
	}
	const fileInputRef = useRef<HTMLInputElement>(null)

	// isScanningUrl tracks the initial HTTP request in flight.
	// waitingForGeneration bridges the gap between request completion and isGenerating becoming true.
	const [isScanningUrl, setIsScanningUrl] = useState(false)
	const [urlError, setUrlError] = useState<string | null>(null)
	const [scanErrorDismissedForId, setScanErrorDismissedForId] = useState<string | null>(null)
	const waitingForGeneration = useRef(false)
	const prevIsGenerating = useRef(isGenerating)

	// Detect when isGenerating turns on - clear the waiting flag
	if (isGenerating && !prevIsGenerating.current) {
		waitingForGeneration.current = false
	}
	prevIsGenerating.current = isGenerating

	// Derived loading state: true if request in flight, waiting for generation, or generating
	const isScanning = isScanningUrl || waitingForGeneration.current || isGenerating

	const handleScanUrl = () => {
		// Normalize and validate before scanning
		const normalized = normalizeUrl(urlValue)
		if (!normalized) {
			return // Empty input, nothing to scan
		}

		const validationError = validateUrl(urlValue)
		if (validationError) {
			setUrlError(validationError)
			return
		}

		setUrlError(null)
		setScanErrorDismissedForId(null)
		waitingForGeneration.current = true
		setIsScanningUrl(true)
		// Pass the normalized URL to the API
		onScanUrl?.(normalized, () => {
			setIsScanningUrl(false)
		}, (errorKey) => {
			waitingForGeneration.current = false
			setIsScanningUrl(false)
			setUrlError(errorKey)
		})
	}

	const handleUrlChange = (value: string) => {
		onUrlChange(value)
		// Clear error when user types
		if (urlError) {
			setUrlError(null)
		}
		setScanErrorDismissedForId(branding?.id ?? null)
	}

	const handleUrlKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && onScanUrl && !isScanning && !isChangingLogo) {
			const normalized = normalizeUrl(urlValue)
			const validationError = validateUrl(urlValue)

			if (normalized && !validationError) {
				e.preventDefault()
				handleScanUrl()
			}
		}
	}

	const urlValidationError = validateUrl(urlValue)
	const normalizedUrlValue = normalizeUrl(urlValue)
	const isUrlValid = normalizedUrlValue !== null && !urlValidationError
	const isScanErrorDismissed = !!branding?.id && scanErrorDismissedForId === branding.id
	const shownUrlError = urlError ?? (isScanning || isScanErrorDismissed ? null : scanError)

	if (!branding || isPending) {
		return <div>{t('common.loading')}</div>
	}

	const resolvedCornerRadiusValue: CornerRadius = cornerRadiusValue ?? 'large'

	return (
		<div className="p-4 grid gap-8 h-[max-content]">
			{/* Brand Name Section */}
			{onNameChange !== undefined && (
				<TextField
					label={t('branding.name')}
					value={nameValue ?? ''}
					onChange={onNameChange}
					placeholder={t('branding.namePlaceholder')}
				/>
			)}

			{/* URL Scanning Section */}
			{enableScanning && (
				<div className="flex gap-2 p-4 rounded-lg bg-purple-50 ring-1 ring-purple-100">
					<div className="flex-1">
						<TextField
							label={t('branding.scanWebsiteUrl')}
							value={urlValue}
							onChange={handleUrlChange}
							onKeyDown={handleUrlKeyDown}
							placeholder={t('branding.urlPlaceholder')}
							type="text"
							errors={shownUrlError ? [t(shownUrlError)] : []}
							toolbar={
								<Button
									onClick={handleScanUrl}
									disabled={!urlValue || !onScanUrl || !isUrlValid || isChangingLogo}
									type="colorful"
									size="short"
									isLoading={isScanning}
								>
									{t('branding.scan')}
								</Button>
							}
							hint={shownUrlError ? undefined : (
								<div className="flex flex-col gap-2">
									<div>
										{t('branding.scanHintPart1')}{' '}
										<strong>
											{t('branding.scanHintOverwrite')}
										</strong>
										{t('branding.scanHintPart2')}
									</div>
									<div>
										{t('branding.mostRecentScan')} {branding?.website || t('branding.none')}
									</div>
								</div>
							)}
						/>
					</div>
				</div>
			)}

			{/* Brand Logo Section */}
			<div className="flex gap-2 items-center">
				<StatefulImage
					src={branding?.logo?.url || ''}
					alt={t('branding.logoAlt')}
					className="rounded bg-grey-100 size-32 cursor-pointer"
					forceIsLoading={isChangingLogo}
					isGenerating={isScanning}
					generatingPlaceholder={<div className="text-sm">{t('branding.scanning')}</div>}
					objectFit="contain"
					onClick={isScanning ? undefined : () => fileInputRef.current?.click()}
					showCheckerboard={true}
				/>
				<div className="flex flex-col gap-1">
					<Button
						type="subtle"
						size="short"
						onClick={() => fileInputRef.current?.click()}
						isLoading={isChangingLogo || isScanning}
					>
						{t('branding.upload')}
					</Button>
					<div className="text-[11px] text-grey-500 text-center">
						{t('branding.uploadHint')}
					</div>
				</div>
			</div>
			<input
				type="file"
				accept="image/*"
				className="hidden"
				ref={fileInputRef}
				onChange={(e) => {
					if (e.target.files?.[0]) {
						handleLogoUpload(e.target.files[0])
					}
				}}
			/>

			{/* Typography Section */}
			{/* Disabled because fonts aren't yet fully set up */}
			{
				/* <div className="grid grid-cols-3 gap-4">
				<WidgetLabel label="Corner Radius">
					<WidgetSelectDropdown
						options={cornerRadiusOptions}
						value={resolvedCornerRadiusValue}
						onChange={(value) => {
							onCornerRadiusChange?.(value)
						}}
						label="Corner Radius"
					/>
				</WidgetLabel>
				<WidgetLabel label="Title">
					<WidgetFontSelectDropdown
						value={branding?.titleFont || 'Onest'}
						onChange={(value) => {
							onChange({ ...branding, titleFont: value })
						}}
					/>
				</WidgetLabel>
				<WidgetLabel label="Body">
					<WidgetFontSelectDropdown
						value={branding?.bodyFont || 'Onest'}
						onChange={(value) => {
							onChange({ ...branding, bodyFont: value })
						}}
					/>
				</WidgetLabel>
			</div> */
			}

			{/* Colors Section */}
			<GenericField label={t('branding.colors')}>
				{/* Single Color Palette Row */}
				<div className="grow-1 grid grid-cols-4 gap-4">
					<WidgetLabel label={t('branding.background')}>
						<WidgetColorField
							value={branding?.background ?? '#FFFFFF'}
							onChange={(color) => handlePaletteColorChange('background', color)}
							label=""
							hidePresets
							isGenerating={isGenerating}
						/>
					</WidgetLabel>

					<WidgetLabel label={t('branding.foreground')}>
						<WidgetColorField
							value={branding?.foreground ?? '#000000'}
							onChange={(color) => handlePaletteColorChange('foreground', color)}
							label=""
							hidePresets
							isGenerating={isGenerating}
						/>
					</WidgetLabel>

					<WidgetLabel label={t('branding.accentBackground')}>
						<WidgetColorField
							value={branding?.accentBackground ?? '#000000'}
							onChange={(color) => handlePaletteColorChange('accentBackground', color)}
							label=""
							hidePresets
							isGenerating={isGenerating}
						/>
					</WidgetLabel>

					<WidgetLabel label={t('branding.accentForeground')}>
						<WidgetColorField
							value={branding?.accentForeground ?? '#FFFFFF'}
							onChange={(color) => handlePaletteColorChange('accentForeground', color)}
							label=""
							hidePresets
							isGenerating={isGenerating}
						/>
					</WidgetLabel>
				</div>
			</GenericField>

			{/* Tone of Voice Section */}
			<LargeTextField
				label={t('branding.toneOfVoice')}
				value={branding?.toneOfVoice ?? ''}
				isGenerating={isGenerating}
				onChange={(value) => {
					onChange({ ...branding, toneOfVoice: value })
				}}
			/>

			{/* Visual Guidance Section */}
			<LargeTextField
				label={t('branding.visualGuidance')}
				value={branding?.visualGuidance ?? ''}
				isGenerating={isGenerating}
				onChange={(value) => {
					onChange({ ...branding, visualGuidance: value })
				}}
			/>

			{showKeyImagePreference && (
				<GenericField label={t('branding.keyImageHeading')} wrapLabel={false}>
					<SwitchField
						checked={branding?.generateKeyImage ?? true}
						disabled={isGenerating}
						onChange={(generateKeyImage) => {
							onChange({ ...branding, generateKeyImage })
							logger.track('Branding: AI image default changed', {
								data: {
									branding_id: branding?.id,
									generate_key_image: generateKeyImage ? 'yes' : 'no',
								},
							})
						}}
						label={t('branding.generateKeyImage')}
						description={t('branding.generateKeyImageHint')}
					/>
				</GenericField>
			)}
		</div>
	)
}
